import React, { useState, useEffect } from 'react';
import type { ZodType } from 'zod';

const session_token = localStorage.getItem('SessionToken');

export function uploadFetch<T = unknown>(
  endpoint: string,
  isNew: boolean,
  uploadlObject: unknown,
  setFunction: (data: T) => void,
  setWasSuccessfullyUploaded?: (success: boolean) => void,
  wasSaved?: (saved: boolean) => void,
  setIsLoading?: (loading: boolean) => void
) {
  if (setIsLoading) {
    setIsLoading(true);
  }

  const requestOptions = {
    method: isNew ? 'POST' : 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + session_token,
    },
    body: JSON.stringify(uploadlObject),
  };
  fetch(`/api/${endpoint}`, requestOptions)
    .then(res => {
      if (res.status === 200) {
        return res.json();
      } else if (res.status === 408) {
        window.location.reload();
      } else {
        throw Error(
          `ERROR ${isNew ? 'POST' : 'PUT'} in ${endpoint}: ${res.status}`
        );
      }
    })
    .then(res => {
      if (setWasSuccessfullyUploaded) {
        setWasSuccessfullyUploaded(true);
      }
      if (wasSaved) {
        wasSaved(true);
      }
      if (setIsLoading) {
        setIsLoading(false);
      }
      setFunction(res);
    })
    .catch(error => {
      console.error(error);
      if (setWasSuccessfullyUploaded) {
        setWasSuccessfullyUploaded(false);
      }
      if (wasSaved) {
        wasSaved(false);
      }
      if (setIsLoading) {
        setIsLoading(false);
      }
    });
}

export function getFetch<S>(
  endpoint: string,
  idToGet: number | string | undefined | null,
  setFunction?: (data: S) => void,
  setWasSuccessfully?: (success: boolean) => void
) {
  let targetEndpoint = `/api/${endpoint}`;
  let tryFetch = true;

  if (endpoint.includes('/') && !endpoint.includes('?')) {
    targetEndpoint = targetEndpoint + `${idToGet}`;

    tryFetch = idToGet !== undefined;
  }

  const requestOptions = {
    headers: { Authorization: 'Bearer ' + session_token },
  };

  if (!tryFetch) {
    return null;
  } else {
    fetch(targetEndpoint, requestOptions)
      .then(res => {
        if (res.status === 200) {
          return res.json();
        } else if (res.status === 204) {
          return null;
        } else if (res.status === 408) {
          window.location.reload();
        } else {
          throw Error(`ERROR GET in ${endpoint}: ${res.status}`);
        }
      })
      .then(res => {
        if (setFunction) {
          setFunction(res);
        }
        if (setWasSuccessfully) {
          setWasSuccessfully(true);
        }
      })
      .catch(error => {
        console.error(error);
        if (setWasSuccessfully) {
          setWasSuccessfully(false);
        }
      });
  }
}

export function useFetch<S>(
  endpoint: string,
  targetId: number | string | null | undefined = undefined
): [
  S | undefined,
  React.Dispatch<React.SetStateAction<S | undefined>>,
  boolean
] {
  const [responseValue, setResponseValue] = useState<S>();
  const [wasSuccessfully, setWasSuccessfully] = useState(true);

  const requestOptions = {
    headers: {
      Authorization: 'Bearer ' + session_token,
      'X-APIKEY-LOCAL': '2YoqmmvC5upGaBedZ12ES8pZYj7FZqY',
    },
  };

  useEffect(() => {
    let targetEndpoint = `/api${endpoint}`;
    let execute = true;

    if (targetId !== undefined) {
      if (targetId === null || targetId === -1) {
        execute = false;
      } else {
        targetEndpoint = targetEndpoint + `${targetId}`;
      }
    }

    if (execute) {
      fetch(targetEndpoint, requestOptions)
        .then(res => {
          if (res.status === 200) {
            return res.json();
          } else if (res.status === 204) {
            return null;
          } else if (res.status === 408) {
            window.location.reload();
          } else {
            throw Error(`ERROR GET in ${endpoint}: ${res.status}`);
          }
        })
        .then(res => {
          setResponseValue(res);
        })
        .catch(error => {
          console.error(error);
          setWasSuccessfully(false);
        });
    }
  }, []);

  return [responseValue, setResponseValue, wasSuccessfully];
}

export class ApiError extends Error {
  status: number;
  body: unknown;
  endpoint: string;

  constructor(
    message: string,
    status: number,
    body: unknown,
    endpoint: string
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
    this.endpoint = endpoint;
  }
}

/**
 * Liefert die Fehlermeldung des Backends, wenn eine Mandantenaktion an einem
 * geschlossenen Vorgang (storniert/abgeschlossen) abgelehnt wurde
 * (HTTP 410 Gone, Ticket #591). Gibt null zurück, wenn der Fehler eine
 * andere Ursache hat.
 */
export function getClosedLegalTransactionMessage(
  error: unknown
): string | null {
  if (error instanceof ApiError && error.status === 410) {
    const detail = (error.body as { detail?: unknown } | null)?.detail;
    return typeof detail === 'string' && detail !== ''
      ? detail
      : 'Der Vorgang ist geschlossen. Bitte wenden Sie sich mit Ihrem Anliegen direkt an unser Büro.';
  }
  return null;
}

/**
 * Wird geworfen, wenn die Backend-Antwort nicht zum übergebenen Zod-Schema
 * passt. Enthält den parsierten Roh-Body, damit die Diskrepanz im Detail
 * nachvollziehbar ist.
 */
export class ApiSchemaError extends Error {
  endpoint: string;
  rawBody: unknown;
  issues: unknown;

  constructor(endpoint: string, rawBody: unknown, issues: unknown) {
    super(`Schema mismatch on ${endpoint}`);
    this.name = 'ApiSchemaError';
    this.endpoint = endpoint;
    this.rawBody = rawBody;
    this.issues = issues;
  }
}

export interface CustomFetcherArgs<TSchema extends ZodType | undefined = undefined> {
  endpoint: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /**
   * Optionales Zod-Schema für die Antwort. Wenn gesetzt, wird die Antwort
   * `.parse()`'d und der Rückgabetyp ist der inferierte Schema-Typ. Bei
   * Schema-Verletzungen wird `ApiSchemaError` geworfen.
   */
  schema?: TSchema;
}

export async function customFetcher<TSchema extends ZodType>(
  args: CustomFetcherArgs<TSchema>
): Promise<ReturnType<TSchema['parse']>>;
export async function customFetcher(
  args: CustomFetcherArgs<undefined>
): Promise<unknown>;
export async function customFetcher({
  endpoint,
  method = 'GET',
  body,
  signal,
  schema,
}: CustomFetcherArgs<ZodType | undefined>): Promise<unknown> {
  const sessionToken = localStorage.getItem('SessionToken');
  const apiEndpoint = `/api${endpoint}`;

  const requestOptions: RequestInit = {
    method,
    body: body ? JSON.stringify(body) : null,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${sessionToken}`,
      'X-APIKEY-LOCAL': '2YoqmmvC5upGaBedZ12ES8pZYj7FZqY',
    },
    signal,
  };

  const response = await fetch(apiEndpoint, requestOptions);

  if (response.status === 408) {
    window.location.reload();
    throw new ApiError('Session timeout', 408, null, endpoint);
  }

  if (!response.ok) {
    let parsedBody: unknown = null;
    try {
      const text = await response.text();
      try {
        parsedBody = text ? JSON.parse(text) : null;
      } catch {
        parsedBody = text;
      }
    } catch {
      parsedBody = null;
    }
    throw new ApiError(
      `${method} ${endpoint} failed with status ${response.status}`,
      response.status,
      parsedBody,
      endpoint
    );
  }

  if (response.status === 204) {
    return null;
  }

  const json = await response.json();

  if (schema) {
    const result = schema.safeParse(json);
    if (!result.success) {
      console.error(
        `Schema-Verletzung in ${method} ${endpoint}:`,
        result.error.issues
      );
      throw new ApiSchemaError(endpoint, json, result.error.issues);
    }
    return result.data;
  }

  return json;
}
